import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtUSDT } from "@/components/common";

export default function AdminAdjust() {
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ direction: "add", amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const search = (val) => {
    setQ(val);
    if (val) api.get(`/admin/customers?q=${encodeURIComponent(val)}`).then((r) => setCustomers(r.data)).catch(() => {});
    else setCustomers([]);
  };

  const pick = async (c) => {
    const { data } = await api.get(`/admin/customers/${c.id}`);
    setSelected({ ...c, wallet: data.wallet });
    setCustomers([]); setQ(c.name);
  };

  const projected = () => {
    if (!selected) return null;
    const avail = parseFloat(selected.wallet.available);
    const amt = parseFloat(form.amount || 0);
    return form.direction === "add" ? avail + amt : avail - amt;
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/wallet/adjust", { customer_id: selected.id, ...form });
      toast.success(`Applied · ${data.reference}`);
      setResult(data); setForm({ direction: "add", amount: "", reason: "" });
      setSelected({ ...selected, wallet: data.wallet });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Wallet Adjustment</h1>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        Adjustments follow normal balance rules (no forced negative) and are recorded as compensating ledger entries. A reason is mandatory.
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <label className="block"><span className="text-sm font-medium text-slate-700">Search customer</span>
          <input value={q} onChange={(e) => search(e.target.value)} data-testid="adjust-customer-search"
            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none" placeholder="name or email" /></label>
        {customers.length > 0 && (
          <div className="border border-slate-200 rounded-xl divide-y max-h-48 overflow-y-auto">
            {customers.map((c) => <button key={c.id} onClick={() => pick(c)} data-testid={`adjust-pick-${c.email}`} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">{c.name} · {c.email}</button>)}
          </div>
        )}

        {selected && (
          <form onSubmit={submit} className="space-y-4 border-t border-slate-200 pt-4">
            <div className="grid grid-cols-3 gap-3 text-sm" data-testid="adjust-current-balances">
              <div className="bg-blue-50 rounded-lg p-3"><div className="text-xs text-slate-500">Available</div><div className="font-mono font-bold text-blue-700">{fmtUSDT(selected.wallet.available)}</div></div>
              <div className="bg-amber-50 rounded-lg p-3"><div className="text-xs text-slate-500">Held</div><div className="font-mono font-bold text-amber-700">{fmtUSDT(selected.wallet.held)}</div></div>
              <div className="bg-rose-50 rounded-lg p-3"><div className="text-xs text-slate-500">Deficit</div><div className="font-mono font-bold text-rose-700">{fmtUSDT(selected.wallet.deficit)}</div></div>
            </div>
            <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
              <button type="button" onClick={() => setForm({ ...form, direction: "add" })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${form.direction === "add" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`} data-testid="adjust-add-tab">Add USDT</button>
              <button type="button" onClick={() => setForm({ ...form, direction: "deduct" })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${form.direction === "deduct" ? "bg-white text-rose-700 shadow-sm" : "text-slate-500"}`} data-testid="adjust-deduct-tab">Deduct USDT</button>
            </div>
            <label className="block"><span className="text-sm font-medium text-slate-700">Amount (USDT)</span>
              <input type="number" step="0.000001" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="adjust-amount-input" className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 font-mono outline-none" /></label>
            {form.amount && <div className="text-sm text-slate-600">Projected available: <span className="font-mono font-bold">{fmtUSDT(projected())}</span></div>}
            <label className="block"><span className="text-sm font-medium text-slate-700">Reason (required)</span>
              <input required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                data-testid="adjust-reason-input" className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none" /></label>
            <button disabled={busy} data-testid="adjust-submit-button" className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-60">Apply adjustment</button>
          </form>
        )}
        {result && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3" data-testid="adjust-result">Done · Ref {result.reference} · New available {fmtUSDT(result.wallet.available)}</div>}
      </div>
    </div>
  );
}
